import axios, { AxiosRequestConfig, AxiosError } from 'axios'
import { camelizeKeys } from 'humps'

import type { RawOrder, RawInvoice } from './types'
import cookieStorage from 'services/cookies'
import { parseOrder, parseInvoice } from './parse'

const COMMERCE_API = process.env.COMMERCE_API

const request = axios.create({
  baseURL: COMMERCE_API,
})

request.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = cookieStorage.getAccessToken()

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

const MERCHANT = 'shopify'

export async function claimOrder(
  orderId: string,
  userId: string,
  claimToken?: string,
) {
  let url = `orders/${MERCHANT}/${orderId}`

  if (claimToken) {
    url += `?claim_token_string=${claimToken}`
  }

  const response = await request
    .patch(url, {
      purchaser_user_id: Number(userId),
    })
    .then((response) => (camelizeKeys(response.data) as unknown) as RawOrder)
    .catch((error: AxiosError) => {
      if (error.response.status === 400) {
        throw new Error('claim window has expired')
      }

      throw new Error(
        `claiming order ${orderId} failed with ${error.response?.status}`,
      )
    })

  return parseOrder(response)
}

export async function sendReclaimToken(orderId: string, userId: string) {
  return request
    .post(`orders/send_reclaim_token/${MERCHANT}/${orderId}`, {
      purchaser_user_id: Number(userId),
    })
    .catch((error: AxiosError) => {
      throw new Error(
        `sending reclaim token for order ${orderId} failed with ${error.response?.status}`,
      )
    })
}

export async function fetchInvoice(invoiceId: number) {
  const response = await request
    .get(`invoices/${invoiceId}`)
    .then((response) => (camelizeKeys(response.data) as unknown) as RawInvoice)
    .catch((error: AxiosError) => {
      throw new Error(
        `fetching invoice ${invoiceId} failed with ${error.response?.status}`,
      )
    })

  return parseInvoice(response)
}

export async function fulfillItem(invoiceId: number, itemId: number) {
  const response = await request
    .post(`invoices/${invoiceId}/line_items/${itemId}/fulfill()`, {
      quantity: 1,
    })
    .then((response) => camelizeKeys(response.data))
    .catch((error: AxiosError) => {
      throw new Error(
        `fulfilling invoice ${invoiceId} and item number ${itemId} failed with ${error.response?.status}`,
      )
    })

  return response
}
